// Helpers shared by the fetch, XHR, beacon and WebSocket hooks.

import { truncate } from '../shared/serialise.js';

export function absoluteUrl(url) {
  try {
    return new URL(String(url), location.href).href;
  } catch {
    return String(url);
  }
}

/** Same-origin URLs are shown as path + query; others in full. */
export function shortUrl(url, max = 120) {
  try {
    const parsed = new URL(url, location.href);
    const out = parsed.origin === location.origin ? `${parsed.pathname}${parsed.search}` : parsed.href;
    return truncate(out, max);
  } catch {
    return truncate(String(url), max);
  }
}

/** @returns {Record<string, string>} */
export function headersToObject(headers) {
  const out = {};
  if (!headers) return out;
  try {
    new Headers(headers).forEach((value, name) => {
      out[name] = value;
    });
  } catch {
    // unusual header init; ignore
  }
  return out;
}

/** Parses XMLHttpRequest.getAllResponseHeaders(). */
export function parseRawHeaders(raw) {
  const out = {};
  if (!raw) return out;
  for (const line of String(raw)
    .trim()
    .split(/[\r\n]+/)) {
    const index = line.indexOf(':');
    if (index <= 0) continue;
    const name = line.slice(0, index).trim().toLowerCase();
    const value = line.slice(index + 1).trim();
    out[name] = out[name] ? `${out[name]}, ${value}` : value;
  }
  return out;
}

/** Content types we read as text. An unknown type is treated as text. */
export function isTextual(contentType) {
  if (!contentType) return true;
  return /^text\/|json|xml|javascript|ecmascript|graphql|x-www-form-urlencoded|csv|yaml/i.test(contentType);
}

/**
 * Describes a request body without consuming it.
 * @returns {{ body?: string, truncated?: boolean, note?: string, fields?: Array<[string, string]> }}
 */
export function bodySummary(body, max) {
  if (body === undefined || body === null) return {};
  try {
    if (typeof body === 'string') {
      return { body: body.slice(0, max), truncated: body.length > max || undefined };
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      const text = body.toString();
      return { body: text.slice(0, max), truncated: text.length > max || undefined };
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const fields = [];
      for (const [name, value] of body.entries()) {
        if (fields.length >= 100) break;
        fields.push([
          name,
          typeof value === 'string'
            ? truncate(value, 500)
            : `[File ${value.name || 'blob'} ${value.type || 'unknown type'} ${value.size} bytes]`,
        ]);
      }
      return { note: 'multipart/form-data', fields };
    }
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      return { note: `[Blob ${body.type || 'unknown type'} ${body.size} bytes]` };
    }
    if (body instanceof ArrayBuffer) return { note: `[binary ${body.byteLength} bytes]` };
    if (ArrayBuffer.isView(body)) return { note: `[binary ${body.byteLength} bytes]` };
    if (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream) {
      return { note: '[stream body not captured]' };
    }
    if (typeof Document !== 'undefined' && body instanceof Document) {
      return { note: '[Document body]' };
    }
    const text = String(body);
    return { body: text.slice(0, max), truncated: text.length > max || undefined };
  } catch (error) {
    return { note: `[body not readable: ${error && error.message}]` };
  }
}

/**
 * Reads up to `max` characters from a stream, then cancels it.
 * @param {ReadableStream<Uint8Array> | null} stream
 * @param {number} max
 */
export async function readLimited(stream, max) {
  if (!stream) return { text: '' };
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let text = '';
  let truncated = false;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      text += decoder.decode(value, { stream: true });
      if (text.length > max) {
        truncated = true;
        text = text.slice(0, max);
        reader.cancel().catch(() => {});
        break;
      }
    }
    if (!truncated) text += decoder.decode();
  } catch (error) {
    return { text, truncated, error: String(error) };
  }
  return { text, truncated };
}

/** GraphQL servers often return errors with HTTP 200. Returns their messages, if any. */
export function graphqlErrors(text) {
  if (typeof text !== 'string' || !text.trimStart().startsWith('{')) return null;
  try {
    const json = JSON.parse(text);
    if (Array.isArray(json.errors) && json.errors.length) {
      return json.errors.slice(0, 5).map((e) => truncate(String((e && e.message) || e), 300));
    }
  } catch {
    // not JSON, or truncated
  }
  return null;
}

/** Summary of a WebSocket or EventSource message payload. */
export function messageSummary(data, max) {
  if (typeof data === 'string') {
    return { data: data.slice(0, max), truncated: data.length > max || undefined, size: data.length };
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return { note: `[Blob ${data.size} bytes]`, size: data.size };
  }
  if (data instanceof ArrayBuffer) return { note: `[binary ${data.byteLength} bytes]`, size: data.byteLength };
  if (ArrayBuffer.isView(data)) return { note: `[binary ${data.byteLength} bytes]`, size: data.byteLength };
  return { note: '[unknown payload]' };
}
